import { createTimeInstance } from '@utils/time';

// Ordering the backfill picker by how recently each spec was edited.
//
// The picker's own payload cannot answer this. GET /score_backfill/specs returns
// `{score_spec_id, description, doc_types, trigger_kind}` and nothing else, already sorted by id —
// there is no timestamp on it at all. The recency lives on the score-spec RECORD, which a different
// route serves, so the page fetches that separately and joins the two here.
//
// That join is why this is a module rather than a `.sort()` at the call site: it is the one place
// that knows the two lists are keyed on `score_spec_id`, and the one place that has to decide what
// happens to a spec the second list never mentioned.
//
// A SPEC WITH NO KNOWN TIMESTAMP KEEPS ITS PLACE AT THE BACK, in the order the server sent it. It is
// not undated — it is unanswered, because the recency read was skipped, failed, or simply did not
// include it. Sorting those as though they were the oldest would assert something no response said.
// This is the rule rowSort states for an empty cell: a null is its own group and sorts last.

// score_spec_id -> updated_at, from the score-spec list records. Prefers `updated_at` and falls back
// to `created_at`, the same pair useScoreSpecs sorts on, so a spec never edited since creation still
// takes part rather than dropping to the back.
export const updatedAtById = (records) => {
  const map = new Map();
  (records || []).forEach((record) => {
    const id = record?.score_spec_id;
    if (!id) return;
    const stamp = record.updated_at || record.created_at;
    if (!stamp) return;
    const instance = createTimeInstance(stamp);
    if (!instance || !instance.isValid()) return;
    map.set(String(id), instance.valueOf());
  });
  return map;
};

// Newest first, then everything the join could not date, in the order it arrived.
//
// Returns the input untouched when nothing is dated — which is the whole no-permission and
// failed-read path, and is why neither needs its own branch at the call site.
export const sortSpecsByRecency = (specs, updatedById) => {
  const list = specs || [];
  if (!updatedById || updatedById.size === 0) return list;

  const dated = [];
  const undated = [];
  list.forEach((spec, index) => {
    const at = updatedById.get(String(spec?.score_spec_id));
    if (at === undefined) undated.push({ spec, index });
    else dated.push({ spec, index, at });
  });

  // Ties break on the server's own order, so two specs saved in the same second do not swap places
  // between renders.
  dated.sort((a, b) => b.at - a.at || a.index - b.index);

  return [...dated, ...undated].map((entry) => entry.spec);
};
